"use client";

import { useState } from "react";
import { Tag, ShieldCheck, Zap, Image as ImageIcon, Sparkles } from "lucide-react";

interface HeroProps {
    onPurchase: (customCoupon?: string) => void;
    isLoading: boolean;
    couponCode: string;
    setCouponCode: (code: string) => void;
}

const Hero: React.FC<HeroProps> = ({ onPurchase, isLoading, couponCode, setCouponCode }) => {
    const [showCouponInput, setShowCouponInput] = useState(!!couponCode);

    const handleApplyCoupon = () => {
        onPurchase(couponCode);
    };

    return (
        <section className="bg-[#050505] text-white pt-20 pb-16 px-4 overflow-hidden">
            <div className="max-w-5xl mx-auto text-center relative">

                {/* Background Glow */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-64 bg-cyan-500/10 blur-[120px] -z-10"></div>

                {/* Badge minimalista - Más urgente */}
                <div className="inline-flex items-center gap-2 bg-cyan-400/10 border border-cyan-400/30 rounded-full px-4 py-1.5 mb-8 animate-fade-in">
                    <Sparkles size={14} className="text-cyan-400" />
                    <span className="text-cyan-400 text-[10px] md:text-xs font-bold uppercase tracking-[0.2em]">
                        Ready to deploy in <span className="underline italic">under 10 minutes</span>
                    </span>
                </div>

                {/* Título Principal - Enfocado en el beneficio */}
                <h1 className="text-5xl md:text-7xl font-black mb-8 leading-[1.1] tracking-tighter">
                    Ship your Waitlist <br />
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">
                        in minutes
                    </span>
                </h1>

                {/* Descripción - Atacando el dolor */}
                <div className="max-w-3xl mx-auto mb-12">
                    <p className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-6">
                        Don't waste your weekend configuring Prisma, a database, and anti-spam filters. Start building your MVP instead.
                    </p>

                    {/* Key Value Props */}
                    <div className="flex flex-wrap justify-center gap-4 text-sm font-medium text-gray-400 mb-10">
                        <span className="flex items-center gap-1.5"><ShieldCheck size={16} className="text-cyan-400" /> Anti-Troll System</span>
                        <span className="flex items-center gap-1.5"><Zap size={16} className="text-cyan-400" /> Next.js 14 Ready</span>
                        <span className="flex items-center gap-1.5"><ImageIcon size={16} className="text-cyan-400" /> AI Image Prompts Included</span>
                    </div>

                    {/* Screenshot con Efecto Premium */}
                    <div className="group relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] transition-transform hover:scale-[1.01] duration-500">
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10 pointer-events-none"></div>
                        <img
                            src="/waitlist-boilerplate/waitlist.png"
                            alt="Waitlist Boilerplate Premium Preview"
                            className="w-full h-auto grayscale-[20%] group-hover:grayscale-0 transition-all duration-700"
                        />
                        <div className="absolute bottom-4 left-6 z-20 flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                            <span className="text-xs font-mono text-gray-300 uppercase tracking-widest">Live Production Example</span>
                        </div>
                    </div>
                </div>

                {/* CTA Principal - Efecto de urgencia/exclusividad */}
                <div className="flex flex-col items-center">
                    <div className="group relative w-full sm:w-auto px-4 sm:px-0">
                        {/* Glow effect - Adjusted for mobile */}
                        <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 to-blue-600 rounded-full blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>

                        <button
                            onClick={() => onPurchase()}
                            disabled={isLoading}
                            className={`relative w-full sm:w-auto bg-cyan-400 hover:bg-cyan-300 text-black font-black 
                                /* Mobile sizes */
                                py-4 px-8 text-lg 
                                /* Desktop sizes */
                                sm:py-5 sm:px-16 sm:text-2xl 
                                rounded-full transition-all active:scale-95 shadow-2xl 
                                ${isLoading ? "opacity-60 cursor-not-allowed" : ""}`}
                        >
                            {isLoading ? "Unlocking Access..." : "Own the Code — $11.99"}
                        </button>
                    </div>

                    {/* Promo Code Logic */}
                    <div className="mt-6 w-full max-w-sm">
                        {!showCouponInput ? (
                            <button
                                onClick={() => setShowCouponInput(true)}
                                className="text-gray-500 hover:text-cyan-400 text-xs flex items-center justify-center gap-2 mx-auto transition-colors tracking-wide"
                            >
                                <Tag size={14} />
                                HAVE A DISCOUNT CODE?
                            </button>
                        ) : (
                            <div className="flex gap-2 animate-in fade-in slide-in-from-bottom-2">
                                <input
                                    type="text"
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                    placeholder="ENTER CODE"
                                    className="flex-1 px-4 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-white text-sm focus:border-cyan-400 outline-none transition-all"
                                />
                                <button
                                    onClick={handleApplyCoupon}
                                    className="bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all"
                                >
                                    Apply
                                </button>
                            </div>
                        )}
                    </div>

                    <p className="text-[10px] text-zinc-500 mt-6 uppercase tracking-[0.2em]">
                        {/* One-time payment · Lifetime Updates · 100% Clean Code */}
                        One-time payment · 100% Clean Code
                    </p>
                </div>

                {/* Feature Grid - Más visual */}
                <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-12 text-left border-t border-white/5 pt-16">
                    <div className="group">
                        <div className="w-12 h-12 bg-cyan-400/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-cyan-400/20 transition-colors">
                            <Zap className="text-cyan-400" />
                        </div>
                        <h3 className="text-white font-bold text-xl mb-2">Ultra-Fast Stack</h3>
                        <p className="text-gray-400 text-md leading-relaxed">Next.js 14, Prisma ORM, and Supabase. Architecture designed for scale and speed.</p>
                    </div>
                    <div className="group">
                        <div className="w-12 h-12 bg-cyan-400/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-cyan-400/20 transition-colors">
                            <ShieldCheck className="text-cyan-400" />
                        </div>
                        <h3 className="text-white font-bold text-xl mb-2">Anti-Gibberish Filter</h3>
                        <p className="text-gray-400 text-md leading-relaxed">Save your database from trolls. Built-in entropy detection for fake emails.</p>
                    </div>
                    <div className="group">
                        <div className="w-12 h-12 bg-cyan-400/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-cyan-400/20 transition-colors">
                            <ImageIcon className="text-cyan-400" />
                        </div>
                        <h3 className="text-white font-bold text-xl mb-2">AI Image Prompts</h3>
                        <p className="text-gray-400 text-md leading-relaxed">Don't have a designer? Use our curated Gemini/Midjourney prompts for your hero section.</p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;